import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getCountFromServer,
  Timestamp,
  QueryConstraint,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { OpinionFilters, Opinion, TopicListResponse } from "../types";
import { algoliaClient, ALGOLIA_INDEX_NAME } from "../services/algolia";
import { updateOpinion, deleteOpinion } from "../services/api";

/**
 * 問題一覧を取得
 * - 検索ワードあり: Algolia
 * - 検索ワードなし: Firestore
 */
const fetchTopics = async (filters: OpinionFilters): Promise<TopicListResponse> => {
  const pageNum = filters.page || 1;
  const limitNum = filters.limit || 20;

  // クエリ条件を構築
  const constraints: QueryConstraint[] = [];

  if (filters.difficulty) {
    constraints.push(where("difficulty", "==", filters.difficulty));
  }
  if (filters.genre) {
    constraints.push(where("genre", "==", filters.genre));
  }
  if (filters.isPublished !== undefined) {
    constraints.push(where("isPublished", "==", filters.isPublished));
  }

  // ソート設定（デフォルトは更新日時の降順）
  const sortByField = filters.sortBy || "updatedAt";
  const sortOrderDirection = filters.sortOrder || "desc";
  constraints.push(orderBy(sortByField, sortOrderDirection));

  // 検索がある場合はAlgoliaを使用
  if (filters.search) {
    // Algoliaフィルター条件を構築
    const algoliaFilters: string[] = [];
    if (filters.difficulty) {
      algoliaFilters.push(`difficulty:${filters.difficulty}`);
    }
    if (filters.genre) {
      algoliaFilters.push(`genre:"${filters.genre}"`);
    }
    if (filters.isPublished !== undefined) {
      algoliaFilters.push(`isPublished:${filters.isPublished}`);
    }

    // Algolia検索を実行
    interface AlgoliaHit {
      objectID: string;
      theme: string;
      sourceText?: string;
      genre: string;
      difficulty: number;
      isPublished: boolean;
      isTopical?: boolean;
      tags?: string[];
      imageUrl?: string;
    }

    const response = await algoliaClient.search<AlgoliaHit>({
      requests: [
        {
          indexName: ALGOLIA_INDEX_NAME,
          query: filters.search,
          page: pageNum - 1, // Algoliaは0始まり
          hitsPerPage: limitNum,
          filters: algoliaFilters.length > 0 ? algoliaFilters.join(" AND ") : undefined,
        },
      ],
    });

    const searchResults = response.results[0];
    if (!("hits" in searchResults)) {
      throw new Error("Unexpected Algolia response format");
    }

    const opinions: Opinion[] = searchResults.hits.map((hit: AlgoliaHit) => ({
      id: hit.objectID,
      theme: hit.theme,
      sourceText: hit.sourceText,
      genre: hit.genre,
      difficulty: hit.difficulty,
      isPublished: hit.isPublished,
      // フラグ導入後に管理画面から保存した topic だけが持つ。バックフィル分は Algolia に載っていない
      isTopical: hit.isTopical,
      tags: hit.tags || [],
      // opinionPatterns は Algolia に載せていない（検索リストでは使わない）
      opinionPatterns: [],
      imageUrl: hit.imageUrl,
      // Algoliaには含まれないフィールドはデフォルト値
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: "",
    }));

    return {
      data: opinions,
      pagination: {
        page: pageNum,
        limit: limitNum,
        total: searchResults.nbHits ?? 0,
        pages: searchResults.nbPages ?? 0,
      },
    };
  }

  // 検索なしの場合は通常のページネーション
  // 総件数取得
  const countSnapshot = await getCountFromServer(
    query(
      collection(db, "topics"),
      ...(filters.difficulty ? [where("difficulty", "==", filters.difficulty)] : []),
      ...(filters.genre ? [where("genre", "==", filters.genre)] : []),
      ...(filters.isPublished !== undefined
        ? [where("isPublished", "==", filters.isPublished)]
        : [])
    )
  );
  const total = countSnapshot.data().count;

  // offsetを使用したページネーション（Firestoreは直接offsetをサポートしないため、スキップ用にlimitを増やす）
  // 簡易実装: 全件取得してスライス
  const dataQuery = query(collection(db, "topics"), ...constraints);
  const snapshot = await getDocs(dataQuery);

  const offset = (pageNum - 1) * limitNum;
  const paginatedDocs = snapshot.docs.slice(offset, offset + limitNum);

  const opinions = paginatedDocs.map((docSnap) => {
    const data = docSnap.data();
    const createdAt = data.createdAt as Timestamp | null;
    const updatedAt = data.updatedAt as Timestamp | null;

    return {
      id: docSnap.id,
      ...data,
      createdAt: createdAt?.toDate?.().toISOString() || null,
      updatedAt: updatedAt?.toDate?.().toISOString() || null,
    } as Opinion;
  });

  return {
    data: opinions,
    pagination: {
      page: pageNum,
      limit: limitNum,
      total,
      pages: Math.ceil(total / limitNum),
    },
  };
};

// 問題一覧取得用カスタムフック
export const useTopics = (filters: OpinionFilters) => {
  return useQuery(["topics", filters], () => fetchTopics(filters), {
    keepPreviousData: true, // ページ切り替え時に前のデータを保持
    staleTime: 600000, // 10分間はキャッシュを新鮮とみなす
  });
};

// 問題削除用カスタムフック（API経由でAlgolia同期）
export const useDeleteTopic = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteOpinion, {
    onSuccess: () => {
      // 削除後にキャッシュを無効化して再取得
      queryClient.invalidateQueries("topics");
    },
  });
};

// 問題の公開状態切り替え用カスタムフック（API経由でAlgolia同期）
export const useToggleTopicPublish = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, isPublished }: { id: string; isPublished: boolean }) =>
      updateOpinion(id, { isPublished }),
    {
      // Optimistic Update: API応答を待たずにUIを即座に更新
      onMutate: async ({ id, isPublished }) => {
        // 進行中のクエリをキャンセル
        await queryClient.cancelQueries("topics");

        // 現在のキャッシュを保存（ロールバック用）
        const previousData = queryClient.getQueriesData("topics");

        // キャッシュを楽観的に更新
        queryClient.setQueriesData<TopicListResponse | undefined>("topics", (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((opinion) =>
              opinion.id === id ? { ...opinion, isPublished } : opinion
            ),
          };
        });

        return { previousData };
      },
      // エラー時はロールバック
      onError: (_error, _variables, context) => {
        if (context?.previousData) {
          context.previousData.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data);
          });
        }
      },
      // 成功・エラーに関わらず、最終的にサーバーと同期
      onSettled: () => {
        queryClient.invalidateQueries("topics");
      },
    }
  );
};
